import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getProductReviews, createReview } from "@/lib/reviews.functions";
import { StarRating } from "./StarRating";
import { Loader2, Star, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

interface ProductReviewsProps {
  handle: string;
  productTitle: string;
  shopifyProductId: number;
}

const reviewsQuery = (handle: string) => ({
  queryKey: ["product-reviews", handle] as const,
  queryFn: () => getProductReviews({ data: { handle } }),
  staleTime: 5 * 60 * 1000,
});

export function ProductReviews({ handle, productTitle, shopifyProductId }: ProductReviewsProps) {
  const queryClient = useQueryClient();
  const fetchReviews = useServerFn(getProductReviews);
  const submitReview = useServerFn(createReview);
  const { data, isLoading } = useQuery(reviewsQuery(handle));
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", rating: 5, title: "", body: "" });

  const reviews = data?.reviews ?? [];
  const average = data?.average ?? 0;
  const count = data?.count ?? 0;

  // Schema.org AggregateRating + Review JSON-LD for rich snippets.
  useEffect(() => {
    if (count === 0) return;
    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org/",
      "@type": "Product",
      name: productTitle,
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: average,
        reviewCount: count,
        bestRating: 5,
        worstRating: 1,
      },
      review: reviews.slice(0, 10).map((r) => ({
        "@type": "Review",
        author: { "@type": "Person", name: r.reviewerName },
        datePublished: r.createdAt?.slice(0, 10),
        reviewRating: { "@type": "Rating", ratingValue: r.rating, bestRating: 5, worstRating: 1 },
        name: r.title || undefined,
        reviewBody: r.body,
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(jsonLd);
    script.id = "product-reviews-jsonld";
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById("product-reviews-jsonld");
      if (el) el.remove();
    };
  }, [average, count, reviews, productTitle]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const result = await submitReview({
        data: {
          shopifyProductId,
          handle,
          name: form.name,
          email: form.email,
          rating: form.rating,
          title: form.title,
          body: form.body,
        },
      });
      if (result.ok) {
        toast.success("Thanks! Your review has been submitted and is pending approval.");
        setForm({ name: "", email: "", rating: 5, title: "", body: "" });
        setShowForm(false);
        queryClient.invalidateQueries({ queryKey: ["product-reviews", handle] });
      } else {
        toast.error(result.error || "Could not submit review");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-12 border-t border-[#D4CCBC] pt-10">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-[0.15em] text-[#2A1510]">
            Reviews
          </h2>
          {count > 0 ? (
            <div className="mt-2 flex items-center gap-2">
              <StarRating rating={average} size={16} />
              <span className="font-body text-sm text-[#2A1510]/70">
                {average.toFixed(1)} · {count} {count === 1 ? "review" : "reviews"}
              </span>
            </div>
          ) : (
            <p className="mt-2 font-body text-sm text-[#2A1510]/60">No reviews yet — be the first.</p>
          )}
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="self-start rounded-sm border border-[#412019] px-4 py-2 font-ui text-xs font-bold uppercase tracking-[0.2em] text-[#412019] transition-colors hover:bg-[#FDFDFD] sm:self-auto"
        >
          {showForm ? "Cancel" : "Write a Review"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mt-6 space-y-4 rounded-sm border border-[#D4CCBC] bg-[#FDFAF3] p-5"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#2A1510]">Name</span>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full rounded-sm border border-[#D4CCBC] bg-white px-3 py-2 font-body text-sm text-[#2A1510] focus:border-[#FA5021] focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#2A1510]">Email</span>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full rounded-sm border border-[#D4CCBC] bg-white px-3 py-2 font-body text-sm text-[#2A1510] focus:border-[#FA5021] focus:outline-none"
              />
            </label>
          </div>
          <div>
            <span className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#2A1510]">Rating</span>
            <div className="mt-1 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setForm({ ...form, rating: n })}
                  aria-label={`${n} stars`}
                  className="p-0.5"
                >
                  <Star
                    size={22}
                    className={n <= form.rating ? "fill-[#FA5021] text-[#FA5021]" : "fill-none text-[#D4CCBC]"}
                    strokeWidth={n <= form.rating ? 0 : 1.5}
                  />
                </button>
              ))}
            </div>
          </div>
          <label className="block">
            <span className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#2A1510]">Title (optional)</span>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 w-full rounded-sm border border-[#D4CCBC] bg-white px-3 py-2 font-body text-sm text-[#2A1510] focus:border-[#FA5021] focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="font-ui text-[11px] font-bold uppercase tracking-[0.2em] text-[#2A1510]">Review</span>
            <textarea
              required
              rows={4}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="mt-1 w-full rounded-sm border border-[#D4CCBC] bg-white px-3 py-2 font-body text-sm text-[#2A1510] focus:border-[#FA5021] focus:outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="btn-cc-accent inline-flex items-center gap-2 tracking-[0.2em]"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Submit Review
          </button>
          <p className="font-ui text-[11px] text-[#2A1510]/60">
            Reviews are checked before they appear. Thanks for sharing your experience.
          </p>
        </form>
      )}

      <div className="mt-8 space-y-6">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-[#8B6F47]" />
          </div>
        ) : reviews.length === 0 ? (
          <p className="font-body text-sm text-[#2A1510]/60">No reviews yet.</p>
        ) : (
          reviews.map((r) => <ReviewCard key={r.id} review={r} />)
        )}
      </div>
    </section>
  );
}

function ReviewCard({ review }: { review: import("@/lib/reviews.functions").PublicReview }) {
  const date = useMemo(() => {
    try {
      return new Date(review.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    } catch {
      return "";
    }
  }, [review.createdAt]);

  return (
    <article className="border-b border-[#D4CCBC] pb-6">
      <div className="flex flex-wrap items-center gap-2">
        <StarRating rating={review.rating} size={14} />
        {review.verified && (
          <span className="inline-flex items-center gap-1 font-ui text-[11px] font-bold uppercase tracking-[0.15em] text-[#8B6F47]">
            <BadgeCheck className="h-3.5 w-3.5" /> Verified
          </span>
        )}
        <span className="font-ui text-xs text-[#2A1510]/50">{date}</span>
      </div>
      {review.title && (
        <h3 className="mt-2 font-heading text-base font-bold uppercase text-[#2A1510]">{review.title}</h3>
      )}
      <p className="mt-1 font-body text-sm leading-relaxed text-[#2A1510]/80">{review.body}</p>
      <p className="mt-2 font-ui text-[11px] font-semibold uppercase tracking-[0.15em] text-[#8B6F47]">
        {review.reviewerName}
      </p>
      {review.pictures.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {review.pictures.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noopener noreferrer">
              <img
                src={url}
                alt={`Review photo ${i + 1}`}
                className="h-16 w-16 rounded-sm border border-[#D4CCBC] object-cover"
                loading="lazy"
              />
            </a>
          ))}
        </div>
      )}
    </article>
  );
}
